import React from 'react';
import { observer } from 'mobx-react-lite';
import { Switch } from 'src/domains/common/components/switch/switch';
import { CustomDropdown, FilterOptionType } from 'src/components/RealityCheckForm/CustomDropdown';
import { SourceOfFundsState } from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFunds.state';
import { SourceOfFundsActionPicker } from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFundsActionPicker';
import {
    SourceOfFundsAmountInput,
    SourceOfFundsDescription,
    SourceOfFundsFieldCell,
    SourceOfFundsFieldError,
    SourceOfFundsFieldRow,
    SourceOfFundsPanelInner,
    SourceOfFundsPanelWrapper,
    SourceOfFundsSection,
    SourceOfFundsSectionTitle,
    SourceOfFundsSelectorWrapper,
    SourceOfFundsSwitchRow,
} from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFunds.style';
import { SourceOfFundsCalculationType } from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFunds.types';
import {
    SOURCE_OF_FUNDS_CALCULATION_TYPE_OPTIONS_ARRAY,
    isSourceOfFundsCalculationType,
} from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFundsThresholdOptions';

interface SourceOfFundsNewAccountPanelPropsType {
    state: SourceOfFundsState;
}

export const SourceOfFundsNewAccountPanel = observer(
    ({ state }: SourceOfFundsNewAccountPanelPropsType): JSX.Element => {
        return (
            <SourceOfFundsSection data-test='source-of-funds-new-account-panel'>
                <SourceOfFundsSectionTitle>New Account Threshold</SourceOfFundsSectionTitle>
                <SourceOfFundsDescription>
                    Sets an early deposit limit for accounts registered within a specific number of days. Exceeding this
                    limit within the account&apos;s early lifecycle changes the SoF status to &quot;requested&quot; and
                    executes selected actions (compliance restriction, email or pop-up).
                </SourceOfFundsDescription>

                <SourceOfFundsSwitchRow>
                    <Switch
                        checked={state.newAccountEnabled}
                        dataTest='source-of-funds-new-account-enabled'
                        label={state.newAccountEnabled ? 'Yes' : 'No'}
                        onChange={(event): void => {
                            state.setNewAccountEnabled(event.target.checked);
                        }}
                    />
                </SourceOfFundsSwitchRow>

                <SourceOfFundsPanelWrapper $isVisible={state.newAccountEnabled}>
                    <SourceOfFundsPanelInner>
                        <SourceOfFundsFieldRow>
                            <SourceOfFundsFieldCell>
                                <span>Account is considered new for</span>
                                <SourceOfFundsFieldError />
                            </SourceOfFundsFieldCell>
                            <SourceOfFundsFieldCell>
                                <SourceOfFundsAmountInput
                                    $hasError={state.newAccountDaysSinceSignupError !== null}
                                    data-test='source-of-funds-new-account-days-since-signup'
                                    inputMode='numeric'
                                    onChange={(event): void => {
                                        state.setNewAccountDaysSinceSignup(event.target.value);
                                    }}
                                    type='text'
                                    value={state.newAccountDaysSinceSignup}
                                />
                                <SourceOfFundsFieldError data-test='source-of-funds-new-account-days-error'>
                                    {state.newAccountDaysSinceSignupError ?? ''}
                                </SourceOfFundsFieldError>
                            </SourceOfFundsFieldCell>
                            <SourceOfFundsFieldCell>
                                <span>days after signup.</span>
                                <SourceOfFundsFieldError />
                            </SourceOfFundsFieldCell>
                        </SourceOfFundsFieldRow>

                        <SourceOfFundsFieldRow>
                            <SourceOfFundsFieldCell>
                                <SourceOfFundsAmountInput
                                    $hasError={state.newAccountAmountError !== null}
                                    data-test='source-of-funds-new-account-amount'
                                    inputMode='numeric'
                                    onChange={(event): void => {
                                        state.setNewAccountAmount(event.target.value);
                                    }}
                                    type='text'
                                    value={state.newAccountAmount}
                                />
                                <SourceOfFundsFieldError data-test='source-of-funds-new-account-amount-error'>
                                    {state.newAccountAmountError ?? ''}
                                </SourceOfFundsFieldError>
                            </SourceOfFundsFieldCell>
                            <SourceOfFundsFieldCell>
                                <span>£</span>
                                <SourceOfFundsFieldError />
                            </SourceOfFundsFieldCell>
                            <SourceOfFundsFieldCell>
                                <SourceOfFundsSelectorWrapper>
                                    <CustomDropdown
                                        dataTest='source-of-funds-new-account-calculation-type'
                                        hasInternalLabel={false}
                                        onChangeCallback={(value): void => {
                                            if (typeof value === 'string' && isSourceOfFundsCalculationType(value)) {
                                                state.setNewAccountCalculationType(value);
                                            }
                                        }}
                                        optionsList={(): Array<FilterOptionType<SourceOfFundsCalculationType>> =>
                                            SOURCE_OF_FUNDS_CALCULATION_TYPE_OPTIONS_ARRAY
                                        }
                                        placeholder='Net/Gross'
                                        selectedValue={(): SourceOfFundsCalculationType =>
                                            state.newAccountCalculationType
                                        }
                                        useMenuPortal
                                    />
                                </SourceOfFundsSelectorWrapper>
                                <SourceOfFundsFieldError />
                            </SourceOfFundsFieldCell>
                            <SourceOfFundsFieldCell>
                                <span>deposits in the last</span>
                                <SourceOfFundsFieldError />
                            </SourceOfFundsFieldCell>
                            <SourceOfFundsFieldCell>
                                <SourceOfFundsAmountInput
                                    $hasError={state.newAccountDepositWindowDaysError !== null}
                                    data-test='source-of-funds-new-account-deposit-window'
                                    inputMode='numeric'
                                    onChange={(event): void => {
                                        state.setNewAccountDepositWindowDays(event.target.value);
                                    }}
                                    type='text'
                                    value={state.newAccountDepositWindowDays}
                                />
                                <SourceOfFundsFieldError data-test='source-of-funds-new-account-deposit-window-error'>
                                    {state.newAccountDepositWindowDaysError ?? ''}
                                </SourceOfFundsFieldError>
                            </SourceOfFundsFieldCell>
                            <SourceOfFundsFieldCell>
                                <span>days</span>
                                <SourceOfFundsFieldError />
                            </SourceOfFundsFieldCell>
                        </SourceOfFundsFieldRow>

                        <SourceOfFundsActionPicker
                            complianceRestrictionPeriod={state.newAccountComplianceRestrictionPeriod}
                            dataTestPrefix='source-of-funds-new-account'
                            onChangeComplianceRestrictionPeriod={state.setNewAccountComplianceRestrictionPeriod}
                            onClearAll={state.clearNewAccountActions}
                            onSetAction={state.setNewAccountAction}
                            selection={state.newAccountActions}
                        />
                    </SourceOfFundsPanelInner>
                </SourceOfFundsPanelWrapper>
            </SourceOfFundsSection>
        );
    }
);
